


// client/src/pages/Investors/InvestorUpload.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { useParams } from "react-router-dom";
import axios from "axios";

import {
  Upload,
  FileText,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ShieldCheck,
} from "lucide-react";

import { toast } from "sonner";

import { PageHeader } from "@/components/common/PageHeader";
import { SectionCard } from "@/components/common/SectionCard";
import { Button } from "@/components/ui/button";

/* ============================================================
   API
   ============================================================ */

const API_URL =
  process.env.REACT_APP_API_URL ||
  "/api";


/* ============================================================
   DOCUMENT CONSTANTS
   ============================================================ */

const DOCUMENT_KEYS = {
  LOWER_TDS_CERTIFICATE: "LOWER_TDS_CERTIFICATE",
  FORM_121: "FORM_121",
};


/* ============================================================
   DOCUMENT LABEL
   ============================================================ */

const getDocumentLabel = (documentKey) => {
  switch (documentKey) {
    case DOCUMENT_KEYS.LOWER_TDS_CERTIFICATE:
      return "Lower TDS Certificate";

    case DOCUMENT_KEYS.FORM_121:
      return "Nil TDS (Form 121)";

    default:
      return documentKey;
  }
};


/* ============================================================
   DOCUMENT DESCRIPTION
   ============================================================ */

const getDocumentDescription = (documentKey) => {
  switch (documentKey) {
    case DOCUMENT_KEYS.LOWER_TDS_CERTIFICATE:
      return "Section 197 certificate for lower TDS deduction.";

    case DOCUMENT_KEYS.FORM_121:
      return "Form 121 for applicable TDS exemption.";

    default:
      return "Applicable TDS document.";
  }
};


/* ============================================================
   NORMALIZE DOCUMENT NAME
   ============================================================ */

const getDocumentKey = (documentName = "") => {
  const value = String(documentName).toLowerCase();

  if (
    value.includes("lower") ||
    value.includes("197") ||
    value.includes("certificate")
  ) {
    return DOCUMENT_KEYS.LOWER_TDS_CERTIFICATE;
  }

  if (
    value.includes("121") ||
    value.includes("nil")
  ) {
    return DOCUMENT_KEYS.FORM_121;
  }

  return documentName;
};


/* ============================================================
   COMPONENT
   ============================================================ */

const InvestorUpload = () => {
  const { token } = useParams();

  const [request, setRequest] = useState(null);

  const [loading, setLoading] = useState(true);

  const [submitting, setSubmitting] = useState(false);

  const [error, setError] = useState("");

  const [submitted, setSubmitted] = useState(false);

  /*
   * Only ONE applicable document is selected.
   *
   * Both options are ALWAYS displayed:
   *
   * 1. Lower TDS Certificate
   * 2. Nil TDS (Form 121)
   */
  const [selectedDocument, setSelectedDocument] =
    useState("");

  const [selectedFile, setSelectedFile] =
    useState(null);


  /* ==========================================================
     LOAD SECURE REQUEST
     ========================================================== */

  useEffect(() => {
    const loadRequest = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          throw new Error(
            "Invalid document submission link."
          );
        }

        const response = await axios.get(
          `${API_URL}/investor/document-request/${encodeURIComponent(
            token
          )}`
        );

        if (!response.data?.success) {
          throw new Error(
            response.data?.message ||
              "Unable to load the document request."
          );
        }

        setRequest(
          response.data.request
        );
      } catch (err) {
        console.error(
          "Failed to load investor document request:",
          err
        );

        const message =
          err.response?.data?.message ||
          err.message ||
          "This document submission link is invalid or has expired.";

        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadRequest();
  }, [token]);


  /* ==========================================================
     DOCUMENT OPTIONS
     ========================================================== */

  /*
   * IMPORTANT:
   *
   * Do NOT depend on request.requiredDocuments for the UI.
   *
   * Older document requests may contain only one document
   * in the database. If we use that value directly, Form 121
   * can disappear from the page.
   *
   * Therefore both applicable document choices are always
   * displayed to the investor.
   */

  const documentOptions = useMemo(() => {
    return [
      DOCUMENT_KEYS.LOWER_TDS_CERTIFICATE,
      DOCUMENT_KEYS.FORM_121,
    ];
  }, []);


  /* ==========================================================
     FILE VALIDATION
     ========================================================== */

  const handleFileChange = (event) => {
    const file =
      event.target.files?.[0];

    if (!file) {
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
    ];

    const fileName =
      file.name.toLowerCase();

    const allowedExtension =
      fileName.endsWith(".pdf") ||
      fileName.endsWith(".jpg") ||
      fileName.endsWith(".jpeg") ||
      fileName.endsWith(".png");

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtension
    ) {
      toast.error(
        "Please upload a PDF, JPG, or PNG file."
      );

      event.target.value = "";

      return;
    }

    const maxSize =
      10 * 1024 * 1024;

    if (file.size > maxSize) {
      toast.error(
        "File size must be 10 MB or less."
      );

      event.target.value = "";

      return;
    }

    setSelectedFile(file);
  };


  /* ==========================================================
     DOCUMENT SELECTION
     ========================================================== */

  const handleDocumentSelection = (
    documentKey
  ) => {
    setSelectedDocument(
      documentKey
    );

    /*
     * Clear previously selected file
     * when document type changes.
     */
    setSelectedFile(null);
  };


  /* ==========================================================
     SUBMIT
     ========================================================== */

  const handleSubmit = async () => {
    if (!selectedDocument) {
      toast.error(
        "Please select the applicable document."
      );

      return;
    }

    if (!selectedFile) {
      toast.error(
        "Please upload the selected document."
      );

      return;
    }

    try {
      setSubmitting(true);

      /*
       * DEMO:
       *
       * The backend currently receives document metadata.
       * Actual document bytes are not persisted by this
       * demonstration endpoint.
       */

      const response =
        await axios.post(
          `${API_URL}/investor/document-request/${encodeURIComponent(
            token
          )}/submit`,
          {
            documents: [
              {
                type: selectedDocument,
                fileName:
                  selectedFile.name,
              },
            ],
          }
        );

      if (!response.data?.success) {
        throw new Error(
          response.data?.message ||
            "Unable to submit document."
        );
      }

      setSubmitted(true);

      toast.success(
        "Document submitted successfully."
      );
    } catch (err) {
      console.error(
        "Document submission failed:",
        err
      );

      const message =
        err.response?.data?.message ||
        err.message ||
        "Unable to submit document.";

      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };


  /* ==========================================================
     LOADING
     ========================================================== */

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="flex flex-col items-center gap-3 text-center">

          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />

          <p className="text-sm text-slate-600">
            Loading your document submission request...
          </p>

        </div>
      </div>
    );
  }


  /* ==========================================================
     ERROR
     ========================================================== */

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">

        <div className="w-full max-w-lg">

          <SectionCard>

            <div className="flex flex-col items-center text-center py-8">

              <div className="rounded-full bg-red-100 p-3 mb-4">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>

              <h1 className="text-xl font-semibold text-slate-900">
                Unable to Open Submission Link
              </h1>

              <p className="mt-3 text-sm text-slate-600">
                {error}
              </p>

              <p className="mt-5 text-xs text-slate-500">
                Please contact the NCD Operations Team
                if you believe this link was sent to
                you incorrectly.
              </p>

            </div>

          </SectionCard>

        </div>

      </div>
    );
  }


  /* ==========================================================
     SUCCESS
     ========================================================== */

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">

        <div className="w-full max-w-lg">

          <SectionCard>

            <div className="flex flex-col items-center text-center py-10">

              <div className="rounded-full bg-green-100 p-4 mb-5">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>

              <h1 className="text-2xl font-semibold text-slate-900">
                Document Received
              </h1>

              <p className="mt-3 text-slate-600">
                Thank you,{" "}
                {request?.investorName}.
              </p>

              <p className="mt-2 text-sm text-slate-500 max-w-md">
                Your document has been successfully
                submitted for the NCD investor
                processing workflow.
              </p>

              <div className="mt-6 rounded-lg bg-slate-50 border px-5 py-4 text-sm text-left w-full">

                <div className="flex justify-between gap-4">

                  <span className="text-slate-500">
                    Series
                  </span>

                  <span className="font-medium text-slate-900">
                    {request?.series || "-"}
                  </span>

                </div>

                <div className="flex justify-between gap-4 mt-2">

                  <span className="text-slate-500">
                    Folio
                  </span>

                  <span className="font-medium text-slate-900">
                    {request?.folio || "-"}
                  </span>

                </div>

                <div className="flex justify-between gap-4 mt-2">

                  <span className="text-slate-500">
                    Document
                  </span>

                  <span className="font-medium text-slate-900">
                    {getDocumentLabel(
                      selectedDocument
                    )}
                  </span>

                </div>

              </div>

              <p className="mt-6 text-xs text-slate-400">
                You may now close this window.
              </p>

            </div>

          </SectionCard>

        </div>

      </div>
    );
  }


  /* ==========================================================
     MAIN INVESTOR PAGE
     ========================================================== */

  return (
    <div className="min-h-screen bg-slate-50">

      <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">

        <PageHeader
          title="Investor Document Submission"
          description="Please provide the applicable TDS document using this secure submission link."
        />

        <div className="mt-6 space-y-6">

          {/* ==================================================
              INVESTOR INFORMATION
              ================================================== */}

          <SectionCard>

            <div className="mb-5">

              <h2 className="text-lg font-semibold text-slate-900">
                Investor Information
              </h2>

              <p className="text-sm text-slate-500 mt-1">
                Please verify the information before
                submitting your document.
              </p>

            </div>


            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              <div className="rounded-lg border bg-white p-4">

                <p className="text-xs text-slate-500">
                  Investor Name
                </p>

                <p className="mt-1 font-medium text-slate-900">
                  {request?.investorName || "-"}
                </p>

              </div>


              <div className="rounded-lg border bg-white p-4">

                <p className="text-xs text-slate-500">
                  Series
                </p>

                <p className="mt-1 font-medium text-slate-900">
                  {request?.series || "-"}
                </p>

              </div>


              <div className="rounded-lg border bg-white p-4">

                <p className="text-xs text-slate-500">
                  Folio
                </p>

                <p className="mt-1 font-medium text-slate-900">
                  {request?.folio || "-"}
                </p>

              </div>

            </div>

          </SectionCard>


          {/* ==================================================
              APPLICABLE DOCUMENT
              ================================================== */}

          <SectionCard>

            <div className="mb-5">

              <div className="flex items-start gap-3">

                <div className="rounded-lg bg-blue-50 p-2">

                  <ShieldCheck className="h-5 w-5 text-blue-600" />

                </div>

                <div>

                  <h2 className="text-lg font-semibold text-slate-900">
                    Upload your applicable document
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Select the document that applies to
                    your TDS status and upload it below.
                  </p>

                </div>

              </div>

            </div>


            {/* ==================================================
                DOCUMENT OPTIONS
                ================================================== */}

            <div className="space-y-3">

              {documentOptions.map(
                (documentKey) => {

                  const selected =
                    selectedDocument ===
                    documentKey;

                  return (
                    <button
                      type="button"
                      key={documentKey}
                      onClick={() =>
                        handleDocumentSelection(
                          documentKey
                        )
                      }
                      className={`w-full rounded-xl border p-4 text-left transition ${
                        selected
                          ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                          : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
                      }`}
                    >

                      <div className="flex items-start gap-3">

                        {/* Radio indicator */}

                        <div
                          className={`mt-0.5 h-5 w-5 rounded-full border flex items-center justify-center ${
                            selected
                              ? "border-blue-600 bg-blue-600"
                              : "border-slate-300 bg-white"
                          }`}
                        >

                          {selected && (
                            <div className="h-2 w-2 rounded-full bg-white" />
                          )}

                        </div>


                        <div>

                          <div className="font-medium text-slate-900">

                            {getDocumentLabel(
                              documentKey
                            )}

                          </div>

                          <div className="mt-1 text-sm text-slate-500">

                            {getDocumentDescription(
                              documentKey
                            )}

                          </div>

                        </div>

                      </div>

                    </button>
                  );
                }
              )}

            </div>


            {/* ==================================================
                FILE UPLOAD
                ================================================== */}

            {selectedDocument && (

              <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">

                <div className="flex items-start gap-3">

                  <div className="rounded-lg bg-blue-50 p-2">

                    <FileText className="h-5 w-5 text-blue-600" />

                  </div>


                  <div>

                    <h3 className="font-medium text-slate-900">

                      {getDocumentLabel(
                        selectedDocument
                      )}

                    </h3>

                    <p className="text-sm text-slate-500 mt-1">

                      {getDocumentDescription(
                        selectedDocument
                      )}

                    </p>

                  </div>

                </div>


                {!selectedFile ? (

                  <div className="mt-5">

                    <label
                      htmlFor="investor-document"
                      className="
                        flex
                        cursor-pointer
                        items-center
                        justify-center
                        rounded-lg
                        border-2
                        border-dashed
                        border-slate-300
                        bg-slate-50
                        px-6
                        py-8
                        transition
                        hover:border-blue-400
                        hover:bg-blue-50
                      "
                    >

                      <div className="text-center">

                        <Upload className="mx-auto h-7 w-7 text-slate-400" />

                        <p className="mt-2 text-sm font-medium text-slate-700">
                          Click to upload
                        </p>

                        <p className="mt-1 text-xs text-slate-500">
                          PDF, JPG or PNG · Maximum 10 MB
                        </p>

                      </div>


                      <input
                        id="investor-document"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        className="hidden"
                        onChange={
                          handleFileChange
                        }
                      />

                    </label>

                  </div>

                ) : (

                  <div className="mt-5 rounded-lg border bg-slate-50 p-4">

                    <div className="flex items-center justify-between gap-4">

                      <div className="flex items-center gap-3 min-w-0">

                        <div className="rounded-lg bg-white p-2 border">

                          <FileText className="h-5 w-5 text-blue-600" />

                        </div>


                        <div className="min-w-0">

                          <p className="text-sm font-medium text-slate-900 truncate">

                            {selectedFile.name}

                          </p>

                          <p className="text-xs text-slate-500 mt-1">

                            {(
                              selectedFile.size /
                              1024 /
                              1024
                            ).toFixed(2)}{" "}
                            MB

                          </p>

                        </div>

                      </div>


                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setSelectedFile(
                            null
                          )
                        }
                      >
                        Remove
                      </Button>

                    </div>

                  </div>

                )}

              </div>

            )}


            {/* ==================================================
                INFORMATION NOTE
                ================================================== */}

            <div className="mt-6 rounded-lg bg-blue-50 border border-blue-100 p-4">

              <p className="text-xs text-blue-800">

                Please select only the document applicable
                to your case. You do not need to upload
                both documents.

              </p>

            </div>

          </SectionCard>


          {/* ==================================================
              SUBMIT
              ================================================== */}

          <SectionCard>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">

              <div>

                <p className="font-medium text-slate-900">
                  Ready to submit?
                </p>

                <p className="text-sm text-slate-500 mt-1">
                  Submit the applicable document for
                  processing.
                </p>

              </div>


              <Button
                onClick={handleSubmit}
                disabled={
                  submitting ||
                  !selectedDocument ||
                  !selectedFile
                }
                className="min-w-[180px]"
              >

                {submitting ? (

                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>

                ) : (

                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Submit Document
                  </>

                )}

              </Button>

            </div>

          </SectionCard>

        </div>

      </div>

    </div>
  );
};


export default InvestorUpload;